import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { typography, spacing, colors } from "../styles/designSystem";
import { PageHeadline, PageSubtext, SectionHeadline } from "../components/Typography";
import {
    CardHeadline,
    CardSubtitle,
    SubsectionHeadline,
    TerminalLabel,
    TerminalLine,
    TerminalPanel,
} from "../components/Typography";
import { primaryButtonBase, secondaryButtonBase } from "../styles/buttonStyles";
import InfoCard from "../components/sub-components/InfoCard";
import MetricsCard from "../components/sub-components/MetricsCard";
import SectionBlock from "../components/sub-components/SectionBlock";
import ToolIconCard from "../components/sub-components/ToolIconCard";
import { Field, Label, Input, Select, Helper, StatusMessage } from "../components/FormComponents";
import ReactLogo from "../pngs/React_(web_framework)-Logo.wine.png";

const NARROW_QUERY = "(max-width: 900px)";

const matchesNarrow = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(NARROW_QUERY).matches;

/**
 * Component Registry.
 *
 * Not a swatch gallery — an inspectable index of what this site is actually built
 * from. Every preview renders the real exported component rather than a mock-up, so
 * the registry cannot drift from the code: if a component changes, this page changes
 * with it. Signatures are listed against the real prop names.
 */

// ===== Demo-only wrappers, so the registry can show the button mixins =====
const DemoPrimary = styled.button`
    ${primaryButtonBase}
`;

const DemoSecondary = styled.button`
    ${secondaryButtonBase}
    color: ${({ theme }) => theme.text};
`;

const REGISTRY = [
    {
        id: "button",
        name: "Button",
        source: "src/styles/buttonStyles.js",
        blurb:
            "Three composable css mixins rather than three components. Callers spread the one they need onto any element — button, anchor, or router Link — and pass a phosphor via the color prop.",
        exports: ["primaryButtonBase", "secondaryButtonBase", "iconButtonBase"],
        signature: [
            ["color", "string", "accent phosphor; defaults to theme.primary"],
            ["disabled", "boolean", "dims and blocks the hover invert"],
        ],
        counts: "3 mixins · 2 props",
        render: () => (
            <DemoRow>
                <DemoPrimary type="button">Primary</DemoPrimary>
                <DemoPrimary type="button" color={colors.amber[400]}>
                    Amber
                </DemoPrimary>
                <DemoPrimary type="button" disabled>
                    Disabled
                </DemoPrimary>
                <DemoSecondary type="button">Secondary</DemoSecondary>
            </DemoRow>
        ),
    },
    {
        id: "typography",
        name: "Typography",
        source: "src/components/Typography.jsx",
        blurb:
            "Every heading and body style on the site, exported as styled components. Headings render as terminal output: bracketed sections, prompted page titles, // subsections.",
        exports: [
            "PageHeadline",
            "SectionHeadline",
            "SubsectionHeadline",
            "CardHeadline",
            "HeroTitle",
            "TerminalLabel",
            "TerminalLine",
            "TerminalPanel",
        ],
        signature: [
            ["accentColor", "string", "SubsectionHeadline rule + marker color"],
            ["color", "string", "TerminalLabel phosphor"],
        ],
        counts: "12 exports · 2 props",
        render: () => (
            <DemoStack>
                <SectionHeadline>Section Headline</SectionHeadline>
                <SubsectionHeadline accentColor={colors.amber[400]}>
                    Subsection Headline
                </SubsectionHeadline>
                <CardHeadline>Card Headline</CardHeadline>
                <CardSubtitle>Card subtitle, for supporting metadata.</CardSubtitle>
                <TerminalLine>Terminal line of output.</TerminalLine>
                <TerminalLabel>Terminal Label</TerminalLabel>
            </DemoStack>
        ),
    },
    {
        id: "panel",
        name: "TerminalPanel",
        source: "src/components/Typography.jsx",
        blurb:
            "The bordered frame used around content blocks. Corner ticks are drawn with pseudo-elements so the panel reads as struck from a character grid rather than a rounded card.",
        exports: ["TerminalPanel"],
        signature: [["children", "ReactNode", "panel contents"]],
        counts: "1 export · 1 prop",
        render: () => (
            <TerminalPanel>
                <TerminalLine>Mounted panel contents.</TerminalLine>
            </TerminalPanel>
        ),
    },
    {
        id: "infocard",
        name: "InfoCard",
        source: "src/components/sub-components/InfoCard.jsx",
        blurb:
            "The readout rail on every case study: dates, roles, and tools as labelled terminal fields.",
        exports: ["InfoCard"],
        signature: [
            ["dates", "string", "service period"],
            ["roles", "string[]", "one line per role"],
            ["tools", "string[]", "joined with commas"],
        ],
        counts: "1 export · 3 props",
        render: () => (
            <InfoCard
                dates="Jun 2021 – Jul 2025"
                roles={["Product Engineer", "Design Systems"]}
                tools={["React", "Node.js", "Figma"]}
            />
        ),
    },
    {
        id: "metrics",
        name: "MetricsCard",
        source: "src/components/sub-components/MetricsCard.jsx",
        blurb:
            "Outcome figures as gauge readouts. Takes an array, so a case study declares its metrics as data rather than markup.",
        exports: ["MetricsCard"],
        signature: [
            ["metrics", "Metric[]", "{ value, label, description, color }"],
        ],
        counts: "1 export · 1 prop",
        render: () => (
            <MetricsCard
                metrics={[
                    {
                        value: "100K+",
                        label: "Employees Supported",
                        description: "Enterprise tooling, nationwide",
                        color: colors.primary[500],
                    },
                    {
                        value: "4 Years",
                        label: "Impact Duration",
                        description: "Continuous delivery",
                        color: colors.amber[400],
                    },
                ]}
            />
        ),
    },
    {
        id: "sectionblock",
        name: "SectionBlock",
        source: "src/components/sub-components/SectionBlock.jsx",
        blurb:
            "A case-study section. Detects bullet-prefixed paragraphs and switches to a chevron list on its own, so authors write prose or bullets without picking a component.",
        exports: ["SectionBlock"],
        signature: [
            ["title", "string", "section heading"],
            ["paragraphs", "string[]", "prose, or • / - prefixed bullets"],
            ["highlight", "string", "callout line, rendered with a > prompt"],
            ["accentColor", "string", "heading rule and marker color"],
        ],
        counts: "1 export · 5 props",
        render: () => (
            <SectionBlock
                title="Example Section"
                accentColor={colors.cyan[300]}
                paragraphs={[
                    "• Detects bullets: a leading marker switches the block to a chevron list.",
                    "• Splits on colons: text before a colon becomes the bullet's title.",
                ]}
                highlight="Highlights render as a bordered terminal callout."
            />
        ),
    },
    {
        id: "form",
        name: "Form Controls",
        source: "src/components/FormComponents.jsx",
        blurb:
            "The contact form's primitives. Inputs are square, phosphor-bordered, and focus to the active theme color; status messages prefix themselves with > OK: or > ERR:.",
        exports: [
            "Row",
            "Field",
            "Label",
            "Input",
            "Select",
            "TextArea",
            "CheckboxRow",
            "Helper",
            "Submit",
            "StatusMessage",
        ],
        signature: [
            ["type", '"success" | "error"', "StatusMessage variant"],
            ["...props", "HTMLAttributes", "native input attributes pass through"],
        ],
        counts: "10 exports · native props",
        render: () => (
            <DemoStack>
                <Field>
                    <Label htmlFor="registry-demo-input">Label</Label>
                    <Input id="registry-demo-input" placeholder="Enter your email..." readOnly />
                    <Helper>Helper text sits under the field.</Helper>
                </Field>
                <Field>
                    <Label htmlFor="registry-demo-select">Select</Label>
                    <Select id="registry-demo-select" defaultValue="30min">
                        <option value="15min">15 minutes</option>
                        <option value="30min">30 minutes</option>
                    </Select>
                </Field>
                <StatusMessage type="success">Message sent.</StatusMessage>
                <StatusMessage type="error">Something went wrong.</StatusMessage>
            </DemoStack>
        ),
    },
    {
        id: "toolicon",
        name: "ToolIconCard",
        source: "src/components/sub-components/ToolIconCard.jsx",
        blurb:
            "A stack entry. The logo is burned into the active phosphor by a theme filter, so third-party brand colors never break the screen.",
        exports: ["ToolIconCard"],
        signature: [
            ["src", "string", "logo asset"],
            ["alt", "string", "image alt text"],
            ["label", "string", "caption"],
        ],
        counts: "1 export · 3 props",
        render: () => (
            <DemoRow>
                <ToolIconCard src={ReactLogo} alt="React" label="React" />
            </DemoRow>
        ),
    },
];

// ===== Layout =====
const PageWrapper = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: ${spacing[900]} ${spacing[700]} ${spacing[1100]};

    @media (max-width: 768px) {
        padding: ${spacing[700]} ${spacing[400]} ${spacing[900]};
    }
`;

const Command = styled.div`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[200]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wider};
    color: ${({ theme }) => theme.primary};
    margin: ${spacing[900]} 0 ${spacing[500]};

    &::before {
        content: "> ";
        opacity: 0.6;
    }
`;

const List = styled.div`
    border-top: 1px solid ${({ theme }) => theme.borderLight};
`;

// One index row. The marker sits in reserved gutter space so selecting a row never
// shifts the text sideways.
const Row = styled.button`
    display: flex;
    align-items: baseline;
    gap: ${spacing[400]};
    width: 100%;
    text-align: left;
    background: ${({ $active, theme }) => ($active ? theme.surfaceHover : "transparent")};
    border: none;
    border-bottom: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 0;
    padding: ${spacing[400]} ${spacing[400]} ${spacing[400]} ${spacing[800]};
    position: relative;
    cursor: pointer;
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[300]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ $active, theme }) => ($active ? theme.primary : theme.text)};
    transition: background 0.14s ease-out, color 0.14s ease-out;

    /* On mobile the row is an accordion header, so the marker turns to point down
       when its panel is open. On desktop it stays a selection caret. */
    &::before {
        content: ${({ $expanded }) => ($expanded ? '"\\25BE"' : '"\\25B8"')};
        position: absolute;
        left: ${spacing[400]};
        color: ${({ theme }) => theme.primary};
        opacity: ${({ $active }) => ($active ? 1 : 0.3)};
    }

    &:hover {
        background: ${({ theme }) => theme.surfaceHover};
        color: ${({ theme }) => theme.primary};

        &::before {
            opacity: 1;
        }
    }

    /* Below the leader-dot cutoff the name and counts would read as a run-on, and a
       two-word name like "Form Controls" wrapped while its counts stayed put. Stack
       them instead. */
    @media (max-width: 640px) {
        flex-direction: column;
        align-items: flex-start;
        gap: ${spacing[150]};
        padding-left: ${spacing[700]};
    }
`;

const Leader = styled.span`
    flex: 1;
    border-bottom: 1px dotted ${({ theme }) => theme.borderMedium};
    transform: translateY(-0.25rem);

    @media (max-width: 640px) {
        display: none;
    }
`;

const Counts = styled.span`
    font-size: ${typography.fontSize[150]};
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.subtleText};
    white-space: nowrap;

    @media (max-width: 640px) {
        font-size: ${typography.fontSize[100]};
    }
`;

const Inspect = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: ${spacing[700]};
    align-items: start;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;

        /* Stacked, the signature's blurb and prop table would push the component
           itself off-screen. Show the thing before the docs for it. */
        > *:last-child {
            order: -1;
        }
    }
`;

const Pane = styled.div`
    border: 1px solid ${({ theme }) => theme.borderMedium};
    background: ${({ theme }) => theme.cardBackground};
    box-shadow: inset 0 0 40px rgba(0, 0, 0, 0.5);
    padding: ${spacing[600]};
    min-width: 0;
`;

const PaneLabel = styled.div`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[150]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.widest};
    color: ${({ theme }) => theme.subtleText};
    padding-bottom: ${spacing[300]};
    margin-bottom: ${spacing[500]};
    border-bottom: 1px solid ${({ theme }) => theme.borderLight};
`;

const Blurb = styled.p`
    font-size: ${typography.fontSize[200]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    margin: 0 0 ${spacing[600]};
`;

// Prop table. Grid rather than <table> so long type signatures wrap instead of
// forcing the pane to scroll horizontally.
const Sig = styled.div`
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr);
    gap: ${spacing[300]} ${spacing[500]};
    font-size: ${typography.fontSize[200]};
    margin-bottom: ${spacing[600]};
`;

const SigName = styled.span`
    color: ${({ theme }) => theme.primary};
`;

const SigType = styled.span`
    color: ${({ theme }) => theme.text};
    word-break: break-word;

    em {
        display: block;
        font-style: normal;
        color: ${({ theme }) => theme.subtleText};
        font-size: ${typography.fontSize[150]};
        margin-top: 2px;
    }
`;

const ExportRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing[200]};
    margin-bottom: ${spacing[600]};
`;

const ExportChip = styled.code`
    font-family: ${typography.fontFamily.primary};
    font-size: ${typography.fontSize[150]};
    color: ${({ theme }) => theme.subtleText};
    border: 1px solid ${({ theme }) => theme.borderLight};
    padding: 2px 8px;
`;

const Source = styled.div`
    font-family: ${typography.fontFamily.primary};
    font-size: ${typography.fontSize[150]};
    color: ${({ theme }) => theme.subtleText};
    border-top: 1px solid ${({ theme }) => theme.borderLight};
    padding-top: ${spacing[300]};
    word-break: break-all;

    &::before {
        content: "SOURCE  ";
        letter-spacing: ${typography.letterSpacing.wide};
        opacity: 0.6;
    }
`;

// Live preview surface. Anything rendered here is the real exported component.
const Stage = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing[500]};
    min-width: 0;
`;

const DemoRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${spacing[400]};
`;

const DemoStack = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${spacing[400]};
    width: 100%;
`;

// On mobile the detail is nested under its row rather than sitting at the bottom of
// the page, so it needs the list's own left gutter and separator.
const Panel = styled.div`
    border-bottom: 1px solid ${({ theme }) => theme.borderLight};
    padding: ${spacing[600]} ${spacing[400]} ${spacing[800]} ${spacing[400]};
    background: ${({ theme }) => theme.background};

    @media (max-width: 640px) {
        padding: ${spacing[500]} 0 ${spacing[700]};
    }
`;

const Note = styled.p`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[150]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wider};
    color: ${({ theme }) => theme.subtleText};
    text-align: center;
    margin: ${spacing[1000]} 0 0;
`;

const Components = () => {
    // Resolved during the first render so mobile never flashes an expanded row.
    const [isNarrow, setIsNarrow] = useState(matchesNarrow);
    // Desktop opens on the first entry; mobile starts fully collapsed so the whole
    // index is scannable before you drill in.
    const [activeId, setActiveId] = useState(() => (matchesNarrow() ? null : REGISTRY[0].id));

    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
        const mq = window.matchMedia(NARROW_QUERY);
        const update = (e) => {
            setIsNarrow(e.matches);
            // Coming back to a wide viewport with nothing open would leave the
            // detail column empty, so re-select the first entry.
            if (!e.matches) setActiveId((current) => current || REGISTRY[0].id);
        };
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, []);

    const active = REGISTRY.find((entry) => entry.id === activeId) || null;

    const select = (id) => {
        // Mobile rows toggle; desktop rows are a single-select list.
        setActiveId((current) => (isNarrow && current === id ? null : id));
    };

    const detail = (entry) => (
        <Inspect>
            <Pane>
                <PaneLabel>Signature</PaneLabel>
                <Blurb>{entry.blurb}</Blurb>

                <ExportRow>
                    {entry.exports.map((name) => (
                        <ExportChip key={name}>{name}</ExportChip>
                    ))}
                </ExportRow>

                <Sig>
                    {entry.signature.map(([name, type, note]) => (
                        <React.Fragment key={name}>
                            <SigName>{name}</SigName>
                            <SigType>
                                {type}
                                {note && <em>{note}</em>}
                            </SigType>
                        </React.Fragment>
                    ))}
                </Sig>

                <Source>{entry.source}</Source>
            </Pane>

            <Pane>
                <PaneLabel>Live Preview</PaneLabel>
                <Stage>{entry.render()}</Stage>
            </Pane>
        </Inspect>
    );

    return (
        <PageWrapper>
            <PageHeadline>Component Registry</PageHeadline>
            <PageSubtext>
                The components this site is built from, inspectable. Every preview renders the real
                exported component rather than a screenshot or a copy, so the registry cannot drift
                from the code &mdash; change a component and this page changes with it.
            </PageSubtext>

            <Command>List Components</Command>
            <List>
                {REGISTRY.map((entry) => {
                    const isActive = entry.id === activeId;
                    const isExpanded = isNarrow && isActive;

                    return (
                        <React.Fragment key={entry.id}>
                            <Row
                                type="button"
                                $active={isActive}
                                $expanded={isExpanded}
                                aria-expanded={isNarrow ? isActive : undefined}
                                aria-pressed={isNarrow ? undefined : isActive}
                                onClick={() => select(entry.id)}
                            >
                                <span>{entry.name}</span>
                                <Leader aria-hidden="true" />
                                <Counts>{entry.counts}</Counts>
                            </Row>
                            {/* Mobile: the detail belongs under the row you tapped. Rendering it
                                at the foot of the page put it a full screen below the fold, so a
                                tap looked like it did nothing. */}
                            {isExpanded && <Panel>{detail(entry)}</Panel>}
                        </React.Fragment>
                    );
                })}
            </List>

            {!isNarrow && active && (
                <>
                    <Command>Inspect {active.name}</Command>
                    {detail(active)}
                </>
            )}

            <Note>One component, infinite combinations</Note>
        </PageWrapper>
    );
};

export default Components;
